// assets/product-custom-property.js
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} [textInput] - The text input.
 * @property {HTMLElement} [characterCount] - The character count element.
 * @property {HTMLInputElement} [dateInput] - The date input.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  handleInput() {
    this.#updateCharacterCount();
  }

  /**
   * Fill the date input from a quick-pick chip (Today / Tomorrow / This weekend).
   * @param {Event} event
   */
  handleQuickPick(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-pick]') : null;
    const input = this.refs.dateInput;
    if (!button || !input) return;

    const date = this.#resolveDate(button.getAttribute('data-pick'));
    if (!date) return;

    input.value = this.#formatDate(date);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    this.#syncActiveChips();
  }

  handleDateChange() {
    this.#syncActiveChips();
  }

  /**
   * Resolve a chip keyword to a Date.
   * @param {string | null} kind
   * @returns {Date | null}
   */
  #resolveDate(kind) {
    const date = new Date();
    date.setHours(0, 0, 0, 0);

    if (kind === 'today') return date;

    if (kind === 'tomorrow') {
      date.setDate(date.getDate() + 1);
      return date;
    }

    if (kind === 'weekend') {
      const day = date.getDay(); // 0 = Sunday ... 6 = Saturday
      if (day !== 0 && day !== 6) {
        date.setDate(date.getDate() + (6 - day));
      }
      return date;
    }

    return null;
  }

  /**
   * Format a Date as YYYY-MM-DD (local time) for a date input.
   * @param {Date} date
   * @returns {string}
   */
  #formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  #syncActiveChips() {
    const input = this.refs.dateInput;
    if (!input) return;

    const chips = this.querySelectorAll('[data-pick]');
    chips.forEach((chip) => {
      const target = this.#resolveDate(chip.getAttribute('data-pick'));
      const isActive = target != null && this.#formatDate(target) === input.value;
      chip.classList.toggle('is-active', isActive);
      chip.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    if (!characterCount || !textInput) return;

    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
